import React, { useState, useEffect } from 'react';
import API from '../api';
import UserAvatar from './UserAvatar';

export default function WeeklyRecap({ user, selectedPool }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const poolId = selectedPool?.id;
        // No pool, no recap. Without this the request went out unscoped and came
        // back with every entry in the database, so somebody in no pool opened the
        // home screen and was shown a "WEEK WINNER" they had never met, over
        // standings for a group they were not in. The Weekly, Season and Recap
        // boards were fixed for this; the home-screen card was missed because it
        // asks the same endpoint from a different place.
        //
        // Rendering nothing is right — the home screen already carries the "your
        // picks aren't scoring yet" nudge, which is the useful thing to say here.
        if (!poolId) { setLoading(false); return; }

        const [lbRes, picksRes] = await Promise.all([
          API.get('/picks/leaderboard?pool_id=' + poolId),
          API.get('/picks/mine')
        ]);
        const lb = lbRes.data.leaderboard || [];
        const week = lbRes.data.week;
        const picks = picksRes.data.picks || [];

        if (!week || lb.length === 0) { setLoading(false); return; }

        // Only recap a week that is actually OVER. Counting the viewer's own
        // finished games was enough to crown a "week winner" on Sunday
        // afternoon with the late games and MNF still to play — use the
        // week-wide game count, the same signal the Recap page uses.
        // Every game, tiebreaker included. It scores like any other game and
        // every board counts it; this card left it out and said 10 wins over a
        // record that read 11 and 5.
        const total = picks.length;
        const totalGames = lbRes.data.totalGames ?? 0;
        const completedGames = lbRes.data.completedGames ?? 0;
        if (!(totalGames > 0 && completedGames >= totalGames)) { setLoading(false); return; }

        // Check if user dismissed this week's recap
        const dismissKey = 'recap_dismissed_' + week.id;
        if (localStorage.getItem(dismissKey)) { setDismissed(true); setLoading(false); return; }

        const winner = lb[0];
        const myEntry = lb.find(e => e.username === user?.username);
        const correct = picks.filter(p => p.is_correct === 1).length;
        const wrong = picks.filter(p => p.is_correct === 0).length;
        // Pushes have to come out of the subtraction too. A tied game is final
        // but correct for nobody, so it is neither correct nor wrong — and
        // "everything left over" would report a settled game as still pending
        // in a recap that only renders once the week is over.
        const pushed = picks.filter(p => p.is_correct === null && p.status === 'final').length;
        const pending = total - correct - wrong - pushed;

        setRecap({ week, winner, myEntry, correct, wrong, pending, total, lb, dismissKey });
      } catch (e) {}
      setLoading(false);
    };
    load();
  }, [user, selectedPool]);

  if (loading || !recap || dismissed) return null;

  const isWinner = recap.winner?.username === user?.username;
  const rankMove = recap.myEntry?.rank;

  const dismiss = () => {
    localStorage.setItem(recap.dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div style={{ background:'#0d1525', border:'1px solid rgba(127,179,255,0.2)', borderRadius:14, padding:'20px', marginBottom:20, position:'relative', overflow:'hidden' }}>
      <style>{`@keyframes shimRecap { 0%{background-position:-200% 0} 100%{background-position:200% 0} }`}</style>

      {/* Dismiss */}
      <button onClick={dismiss} style={{ position:'absolute', top:12, right:14, background:'none', border:'none', color:'#445', cursor:'pointer', fontSize:18, lineHeight:1, padding:4 }}>×</button>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <div style={{ background:'linear-gradient(135deg,#013369,#0a5299)', padding:'6px 14px', borderRadius:20, fontSize:11, fontWeight:700, color:'#7fb3ff', letterSpacing:1 }}>
          📋 {recap.week.label}
        </div>
        <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color:'#fff' }}>WEEKLY RECAP</div>
      </div>

      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        {/* Winner card */}
        <div style={{ flex:'1 1 200px', background: isWinner ? 'linear-gradient(135deg,rgba(255,215,0,0.12),rgba(184,134,11,0.08))' : 'rgba(255,255,255,0.04)', border:'1px solid ' + (isWinner ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)'), borderRadius:12, padding:'16px 18px' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:10 }}>WEEK WINNER</div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ position:'relative' }}>
              <UserAvatar username={recap.winner.username} size={44} />
              <div style={{ position:'absolute', top:-8, left:'50%', transform:'translateX(-50%)', fontSize:14 }}>👑</div>
            </div>
            <div>
              <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:18, fontWeight:700, color: isWinner ? '#FFD700' : '#fff' }}>
                {recap.winner.username}
                {isWinner && <span style={{ fontSize:12, color:'#FFD700', marginLeft:6 }}>that's you!</span>}
              </div>
              <div style={{ fontSize:13, color:'#4caf50', fontWeight:700 }}>{recap.winner.correct} correct picks</div>
            </div>
          </div>
        </div>

        {/* Your stats */}
        {recap.myEntry && (
          <div style={{ flex:'1 1 200px', background:'rgba(1,51,105,0.2)', border:'1px solid rgba(127,179,255,0.15)', borderRadius:12, padding:'16px 18px' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:10 }}>YOUR WEEK</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:26, fontWeight:700, color:'#4caf50' }}>{recap.correct}</div>
                <div style={{ fontSize:11, color:'#445' }}>CORRECT</div>
              </div>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontFamily:"'Oswald',sans-serif", fontSize:26, fontWeight:700, color:'#f44336' }}>{recap.wrong}</div>
                <div style={{ fontSize:11, color:'#445' }}>WRONG</div>
              </div>
            </div>
            <div style={{ marginTop:10, textAlign:'center' }}>
              <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:16, fontWeight:700, color:'#7fb3ff' }}>
                #{recap.myEntry.rank}
              </span>
              <span style={{ fontSize:12, color:'#556', marginLeft:6 }}>of {recap.lb.length} players</span>
            </div>
          </div>
        )}

        {/* Pool mini-standings */}
        <div style={{ flex:'2 1 220px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:'16px 18px' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#556', letterSpacing:1, marginBottom:10 }}>TOP PLAYERS</div>
          {recap.lb.slice(0, 5).map(function(e, i) {
            const isMe = e.user_id != null ? e.user_id === user?.id : e.username === user?.username;
            return (
              <div key={e.entry_id ?? e.user_id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom: i < 4 ? 8 : 0 }}>
                <span style={{ fontFamily:"'Oswald',sans-serif", fontSize:14, fontWeight:700, color: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#445', minWidth:20, textAlign:'right' }}>
                  #{e.rank}
                </span>
                <UserAvatar username={e.username} size={26} />
                <span style={{ fontSize:13, fontWeight:600, color: isMe ? '#7fb3ff' : '#ccd', flex:1 }}>
                  {e.username}{isMe ? ' (you)' : ''}
                </span>
                <span style={{ fontSize:13, fontWeight:700, color:'#4caf50' }}>{e.correct}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
